import { i18n as linguiI18n } from '@lingui/core';
import {
  PlatformLanguageCode,
  platformDefaultLanguageCode,
  supportedPlatformLanguageCodes,
  type SupportedPlatformLanguageCode,
} from '@tov-plus/domain';

import commonEnMessages from './common/en.json';
import commonHeMessages from './common/he.json';
import {
  componentCatalogs,
  type ComponentCatalogs,
  type LocalizationComponent,
} from './catalogs';
export {
  LOCALIZATION_COMPONENTS,
  type LocalizationComponent,
} from './catalogs';

export const SUPPORTED_LOCALES = supportedPlatformLanguageCodes;
export type SupportedLocale = SupportedPlatformLanguageCode;
export interface MissingTranslationDetails {
  id: string;
  locale: SupportedLocale;
}
export type MissingTranslationReporter = (
  details: MissingTranslationDetails,
) => void;

const DEFAULT_LOCALE: SupportedLocale = platformDefaultLanguageCode;
const DEFAULT_COMPONENT: LocalizationComponent = 'common';
const STORAGE_KEY = 'tov_plus_locale';
const QUERY_KEY = 'locale';
let missingTranslationReporter: MissingTranslationReporter | null = null;
let activeComponent: LocalizationComponent = DEFAULT_COMPONENT;

function createActiveCatalogs(component: LocalizationComponent) {
  const componentMessages = componentCatalogs[component];

  return {
    [PlatformLanguageCode.English]: {
      ...componentCatalogs.common[PlatformLanguageCode.English],
      ...componentMessages[PlatformLanguageCode.English],
    },
    [PlatformLanguageCode.Hebrew]: {
      ...componentCatalogs.common[PlatformLanguageCode.Hebrew],
      ...componentMessages[PlatformLanguageCode.Hebrew],
    },
  };
}

let activeCatalogs: ComponentCatalogs = createActiveCatalogs(activeComponent);

function normalizeLocale(input: string | null | undefined): SupportedLocale {
  if (!input) {
    return DEFAULT_LOCALE;
  }

  const lower = input.toLowerCase();
  if (lower.startsWith(PlatformLanguageCode.Hebrew)) {
    return PlatformLanguageCode.Hebrew;
  }

  return platformDefaultLanguageCode;
}

export function resolveInitialLocale() {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE;
  }

  const queryLocale = new URL(window.location.href).searchParams.get(QUERY_KEY);
  if (queryLocale) {
    const normalized = normalizeLocale(queryLocale);
    window.localStorage.setItem(STORAGE_KEY, normalized);
    return normalized;
  }

  const storedLocale = window.localStorage.getItem(STORAGE_KEY);
  if (storedLocale) {
    return normalizeLocale(storedLocale);
  }

  return normalizeLocale(window.navigator.language);
}

let initialized = false;

export function ensureI18nInitialized() {
  if (initialized) {
    return;
  }

  linguiI18n.load({
    [PlatformLanguageCode.English]:
      activeCatalogs[PlatformLanguageCode.English],
    [PlatformLanguageCode.Hebrew]: activeCatalogs[PlatformLanguageCode.Hebrew],
  });
  linguiI18n.activate(resolveInitialLocale());
  initialized = true;
}

export function configureLocalizationComponent(
  component: LocalizationComponent,
) {
  if (activeComponent === component) {
    return;
  }

  activeComponent = component;
  activeCatalogs = createActiveCatalogs(component);

  if (initialized) {
    const locale = getLocale();
    linguiI18n.load({
      [PlatformLanguageCode.English]:
        activeCatalogs[PlatformLanguageCode.English],
      [PlatformLanguageCode.Hebrew]:
        activeCatalogs[PlatformLanguageCode.Hebrew],
    });
    linguiI18n.activate(locale);
  }
}

export function setLocale(locale: SupportedLocale) {
  ensureI18nInitialized();
  linguiI18n.activate(locale);
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, locale);
  }
}

export function getLocale() {
  ensureI18nInitialized();
  return normalizeLocale(linguiI18n.locale);
}

export function getLocaleDirection(
  locale: SupportedLocale = getLocale(),
): 'ltr' | 'rtl' {
  return locale === PlatformLanguageCode.Hebrew ? 'rtl' : 'ltr';
}

function getCatalog(locale: SupportedLocale) {
  return activeCatalogs[locale];
}

function resolveCatalogMessage(id: string, locale: SupportedLocale) {
  const localizedValue = (
    getCatalog(locale) as Record<string, string | undefined>
  )[id];

  if (typeof localizedValue === 'string' && localizedValue.trim().length > 0) {
    return localizedValue;
  }

  const defaultValue = (
    getCatalog(DEFAULT_LOCALE) as Record<string, string | undefined>
  )[id];

  if (typeof defaultValue === 'string' && defaultValue.trim().length > 0) {
    return defaultValue;
  }

  return locale === PlatformLanguageCode.Hebrew
    ? commonHeMessages['general.localization.text_unavailable']
    : commonEnMessages['general.localization.text_unavailable'];
}

function hasTranslation(id: string, locale: SupportedLocale) {
  const value = (getCatalog(locale) as Record<string, string | undefined>)[id];
  return typeof value === 'string' && value.trim().length > 0;
}

function reportMissingTranslation(details: MissingTranslationDetails): void {
  if (!missingTranslationReporter) {
    return;
  }
  missingTranslationReporter(details);
}

export function setMissingTranslationReporter(
  reporter: MissingTranslationReporter | null,
) {
  missingTranslationReporter = reporter;
}

export function msg(id: string) {
  ensureI18nInitialized();
  const locale = getLocale();

  if (!hasTranslation(id, locale)) {
    reportMissingTranslation({
      id,
      locale,
    });
  }

  return resolveCatalogMessage(id, locale);
}

export function msgForLocale(id: string, locale: string | null | undefined) {
  ensureI18nInitialized();
  const normalizedLocale = normalizeLocale(locale);

  if (!hasTranslation(id, normalizedLocale)) {
    reportMissingTranslation({
      id,
      locale: normalizedLocale,
    });
  }

  return resolveCatalogMessage(id, normalizedLocale);
}

/** Keeps maintained data stable within each bundled component/locale view. */
export function createLocaleAwareFactory<T>(factory: () => T): () => T {
  const values = new Map<string, T>();
  return () => {
    const key = `${activeComponent}:${getLocale()}`;
    if (!values.has(key)) values.set(key, factory());
    return values.get(key)!;
  };
}

export type PluralMessageSet = Partial<Record<Intl.LDMLPluralRule, string>> & {
  other: string;
};

export function selectPluralLabel(
  count: number,
  labels: PluralMessageSet,
): string {
  const locale = getLocale();
  const pluralRule = new Intl.PluralRules(locale).select(Math.abs(count));

  return labels[pluralRule] ?? labels.other;
}

export function formatCountLabel(
  count: number,
  labels: PluralMessageSet,
): string {
  return `${count} ${selectPluralLabel(count, labels)}`;
}

export { linguiI18n };
